<script setup lang="ts">
import type { ArticleRecord, CategoryRecord, Maybe } from '~/types/datocms'
import type { CardType } from '~/types'
import type { ArticleListLayout } from './ArticleList.vue'

interface ArticleFilteredListProps {
  type?: CardType
  layout?: ArticleListLayout
  noRecordsMessage?: string
  filterByCategories?: Maybe<Array<ArticleRecord['category']>>
  filterByArticleSlugs?: string[]
  overrideArticles?: ArticleRecord[]
  recordsCount?: Maybe<number>
}

const props = withDefaults(defineProps<ArticleFilteredListProps>(), {
  type: undefined,
  layout: 'news-latest',
  noRecordsMessage: 'No records to display',
  filterByCategories: [] as CategoryRecord[],
  filterByArticleSlugs: [] as string[],
  overrideArticles: [] as ArticleRecord[],
  recordsCount: 3
})

const emit = defineEmits(['loaded'])

const isLoading = ref(true)
const state = reactive({
  relatedArticles: [] as ArticleRecord[]
})

const categoriesFilter = computed(() =>
  props.filterByCategories?.length ? props.filterByCategories.map((x: CategoryRecord) => x.id) : []
)

const slugsFilter = computed(() =>
  props.overrideArticles?.length ? props.overrideArticles.map((x: ArticleRecord) => x.slug) : []
)

const { data: articles } = await useArticles({
  variables: {
    first: props.recordsCount,
    allInCategory: categoriesFilter.value,
    notInSlug: slugsFilter.value.length
      ? (slugsFilter.value as Maybe<string>[])
      : props.filterByArticleSlugs?.length
        ? props.filterByArticleSlugs
        : []
  }
})

if (articles) {
  state.relatedArticles = articles.allArticles as ArticleRecord[]
}

onMounted(() => {
  nextTick(() => {
    if (articles && props.overrideArticles?.length) {
      props.overrideArticles?.forEach(async (article: ArticleRecord) => {
        try {
          const { data } = await useArticles({
            variables: {
              slug: article.slug
            }
          })

          state.relatedArticles.unshift(data.allArticles[0] as ArticleRecord)
          if (state.relatedArticles.length > (props.recordsCount ?? 0)) {
            state.relatedArticles.pop()
          }
        } catch (error) {
          console.error(error)
        }
      })
    }
    emit('loaded')
    isLoading.value = false
  })
})
</script>

<template>
  <ArticleList
    :type="props.type"
    :layout="props.layout"
    :data="state.relatedArticles"
    :is-loading="isLoading"
  />
</template>
